import { useTags } from "@/features/modals/TagsModal/tags";
import { generateTagTranslation } from "@/features/modals/TagsModal/utils";
import { InstallationQuotationExistsFilter } from "@/features/projects/filter/InstallationQuotationExistsFilter";
import { AmountFilterOption } from "@/features/projects/filter/constants";
import { DateFilterAccordionItem } from "@/features/projects/filter/dateFilter/DateFilterAccordionItem";
import { WaitingListFilter } from "@/features/projects/filter/waitingListFilter";
import { formatAmountRange } from "@/utils/formatAmountRange";
import { getCurrencyCode } from "@/utils/getCurrency";
import { mapIdsToNames } from "@/utils/mapIdsToNames";
import {
  type OpportunitiesSearchFilters,
  type OrderMethod,
  isOpportunityStatus,
} from "@repo/api";
import { Accordion, AccordionItem } from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import {
  ModalSheet,
  translateClassification,
  translateConsentFilterStatus,
  translateConsentType,
  translateCustomerType,
  translateInstallationQuotationExists,
  translateInstallationQuotationStatus,
  translateInstallationType,
  translateLanguage,
  translateNbaType,
  translateOpportunityType,
  translateOrderMethod,
  translateOrderStatus,
  translateProjectStatus,
  translateServices,
  translateStage,
  useOrderedCustomerMeetingPoints,
  useOrderedDepartments,
  useOrderedServices,
  useSettings,
} from "@repo/ui";
import { isDefined, trackAnalytics } from "@repo/utils";

import { BokaServiceFilter } from "./BokaServiceFilter";
import { BusinessUnitFilter } from "./BusinessUnitFilter";
import { ClassificationFilter } from "./ClassificationFilter";
import { ConsentFilter } from "./ConsentFilter";
import { CustomerTypeFilter } from "./CustomerTypeFilter";
import { DepartmentFilter } from "./DepartmentFilter";
import { InstallationQuotationStatusFilter } from "./InstallationQuotationStatusFilter";
import { InstallationTypeFilter } from "./InstallationTypeFilter";
import { InterestedServiceFilter } from "./InterestedServiceFilter";
import { NbaFilter } from "./NbaFilter";
import { OrderMethodFilter } from "./OrderMethodFilter";
import { OrderStatusFilter } from "./OrderStatusFilter";
import { PreferredLanguageFilter } from "./PreferredLanguageFilter";
import { StageFilter } from "./StageFilter";
import { StatusFilter } from "./StatusFilter";
import { TagsFilter } from "./TagsFilter";
import { TypeFilter } from "./TypeFilter";
import { ValueFilter } from "./ValueFilter";
import { WorkOrderStatusFilter } from "./WorkOrderStatusFilter";
import { WorkOrderTypeFilter } from "./WorkOrderTypeFilter";
import { NumberSpan } from "./components";
import { DateFilterOption } from "./dateFilter/utils";
import { type ProjectFilterActions } from "./projectFilterReducer";
import { hasActiveFilters } from "./utils/hasActiveFilters";

interface FilterSideBarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  filters: OpportunitiesSearchFilters;
  actions: ProjectFilterActions;
  selectedSection?: string;
  resultSize?: number;
  onResetSort: () => void;
}

function getListSubtitle<K>(
  filterGroup: K[] | undefined,
  list: [string | number, string][]
): string | undefined {
  const items = mapIdsToNames(filterGroup, list);
  return items?.join(", ");
}

function getTranslationSubtitle<K>(
  filterGroup: K[] | undefined,
  translationFunction: (id: K) => string
): string | undefined {
  return filterGroup?.map(translationFunction).join(", ");
}

export const FilterSideBar = ({
  isOpen,
  setIsOpen,
  filters,
  actions,
  selectedSection,
  resultSize,
  onResetSort,
}: FilterSideBarProps) => {
  const { data: storeList } = useOrderedCustomerMeetingPoints();
  const departments = useOrderedDepartments();
  const services = useOrderedServices();
  const { data: tags } = useTags();
  const closeModal = () => setIsOpen(false);
  const {
    general,
    feature_flags,
    waiting_list,
    market,
    language,
    consent_requirements,
    kic_config,
    project_stages,
  } = useSettings();
  const locale = getLanguageLocal(language, market);

  return (
    <ModalSheet
      title={t("label.filter")}
      visible={isOpen}
      onClose={closeModal}
      primaryButtonProps={{
        text: (
          <>
            <span>{t("label.show")}</span>
            {resultSize && <NumberSpan>{resultSize}</NumberSpan>}
          </>
        ),
      }}
      secondaryButtonProps={{
        text: t("action.clear_all"),
        disabled: !hasActiveFilters(filters),
        onClick: () => {
          actions.clearAll();
          onResetSort();
          trackAnalytics("clear_all");
        },
      }}
    >
      <Accordion>
        <DateFilterAccordionItem
          dates={filters.createdDate ?? []}
          actions={actions}
          open={selectedSection === "createdDate"}
          option={DateFilterOption.CREATED_DATE}
          id="created-date-filter"
          title={t("label.created")}
        />
        <DateFilterAccordionItem
          dates={filters.updatedDate ?? []}
          actions={actions}
          open={selectedSection === "updatedDate"}
          option={DateFilterOption.UPDATED_DATE}
          id="updated-date-filter"
          title={t("label.updated")}
        />
        <AccordionItem
          id="opportunity-type-filter"
          title={t("label.type")}
          subtitle={getTranslationSubtitle(
            filters.opportunityType,
            translateOpportunityType
          )}
          open={selectedSection === "opportunityType"}
        >
          <TypeFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="business-unit-filter"
          title={t("label.cmp")}
          subtitle={getListSubtitle(filters.businessUnit, storeList)}
          open={selectedSection === "businessUnit"}
        >
          <BusinessUnitFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="department-filter"
          title={t("label.department")}
          subtitle={getListSubtitle(filters.department, departments)}
          open={selectedSection === "department"}
        >
          <DepartmentFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="status-filter"
          title={t("label.status")}
          subtitle={getTranslationSubtitle(
            [
              ...(filters.status ?? []),
              filters.isNoActionPlanned ? "NO_ACTION_PLANNED" : undefined,
              filters.isColdProject ? "COLD_PROJECT" : undefined,
            ].filter(isDefined),
            (item) => {
              const k = isOpportunityStatus(item)
                ? item?.replaceAll("_", "")
                : item;
              return translateProjectStatus(k as any);
            }
          )}
          open={selectedSection === "status"}
        >
          <StatusFilter filters={filters} actions={actions} />
        </AccordionItem>
        {project_stages?.enable && (
          <AccordionItem
            id="stage-filter"
            title={t("label.stage")}
            subtitle={getTranslationSubtitle(filters.stageFilter, (item) =>
              translateStage(item)
            )}
            open={selectedSection === "stageFilter"}
          >
            <StageFilter filters={filters} actions={actions} />
          </AccordionItem>
        )}
        <AccordionItem
          id="value-filter"
          title={t("label.value")}
          subtitle={formatAmountRange(
            filters.amount,
            getCurrencyCode(general),
            locale,
            Boolean(general?.localization?.disable_decimals)
          )}
          open={selectedSection === "value"}
        >
          <ValueFilter
            filters={filters}
            actions={actions}
            option={AmountFilterOption.VALUE}
          />
        </AccordionItem>
        <AccordionItem
          id="customer-type-filter"
          title={t("label.customer_type")}
          subtitle={getTranslationSubtitle(filters.customerType, (item) =>
            translateCustomerType(item)
          )}
          open={selectedSection === "customer_type"}
        >
          <CustomerTypeFilter filters={filters} actions={actions} />
        </AccordionItem>
        {(general?.supported_languages.length ?? 0) > 1 && (
          <AccordionItem
            id="preferred_language-filter"
            title={t("label.preferred_language")}
            subtitle={getTranslationSubtitle(filters.customerLocales, (item) =>
              translateLanguage(item)
            )}
            open={selectedSection === "preferred_language"}
          >
            <PreferredLanguageFilter filters={filters} actions={actions} />
          </AccordionItem>
        )}
        <AccordionItem
          id="classification-filter"
          title={t("label.classification")}
          subtitle={getTranslationSubtitle(filters.classification, (item) =>
            translateClassification(item)
          )}
          open={selectedSection === "classification"}
        >
          <ClassificationFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="services-filter"
          title={t("label.services_interested")}
          subtitle={mapIdsToNames(filters.services, services)
            ?.map((item) => translateServices(item as string))
            .join(", ")}
          open={selectedSection === "services"}
        >
          <InterestedServiceFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="follow-up-consent-filter"
          title={translateConsentType("FOLLOWUP_CONSENT")}
          subtitle={translateConsentFilterStatus(filters.followUpConsentFilter)}
          open={selectedSection === "followUpConsent"}
        >
          <ConsentFilter
            id="follow-up-consent-sidebar"
            value={filters.followUpConsentFilter}
            onChange={(value) => {
              actions.setFollowUpConsent(value);
              trackAnalytics("select_follow_up_consent_filter");
            }}
          />
        </AccordionItem>
        {consent_requirements?.promotional_consent && (
          <AccordionItem
            id="promotional-consent-filter"
            title={translateConsentType("PROMOTIONAL_CONSENT")}
            subtitle={translateConsentFilterStatus(
              filters.promotionalConsentFilter
            )}
            open={selectedSection === "customerConsent"}
          >
            <ConsentFilter
              id="promotional-consent-sidebar"
              value={filters.promotionalConsentFilter}
              onChange={(value) => {
                actions.setPromotionalConsent(value);
                trackAnalytics("select_promotional_consent_filter");
              }}
            />
          </AccordionItem>
        )}
        {tags && (
          <AccordionItem
            id="tags-filter"
            title={t("label.tags")}
            subtitle={getListSubtitle(
              filters.tags,
              tags.map((item) => [
                item.name,
                item.translationKey
                  ? generateTagTranslation(item, market)
                  : item.name,
              ])
            )}
            open={selectedSection === "tags"}
          >
            <TagsFilter filters={filters} actions={actions} />
          </AccordionItem>
        )}
        <AccordionItem
          id="order-method-filter"
          title={t("label.order_method")}
          subtitle={getTranslationSubtitle(filters.orderMethod, (item) =>
            translateOrderMethod(item as OrderMethod)
          )}
        >
          <OrderMethodFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="order-status-filter"
          title={t("label.order_status")}
          subtitle={getTranslationSubtitle(
            filters.orderStatus,
            (item) => translateOrderStatus(item) ?? item
          )}
        >
          <OrderStatusFilter filters={filters} actions={actions} />
        </AccordionItem>
        <AccordionItem
          id="nba-filter"
          title={t("label.next_best_actions")}
          subtitle={getTranslationSubtitle(
            filters.nbaTypes,
            (item) => translateNbaType(item) ?? item
          )}
        >
          <NbaFilter filters={filters} actions={actions} />
        </AccordionItem>

        <AccordionItem
          id="boka-services-filter"
          title={t("label.boka_services")}
          subtitle={getTranslationSubtitle(
            filters.bokaServices,
            (item) => translateServices(item) ?? ""
          )}
          open={selectedSection === "bokaServices"}
        >
          <BokaServiceFilter filters={filters} actions={actions} />
        </AccordionItem>

        {waiting_list?.waiting_list_enabled && (
          <AccordionItem
            id="waiting-list-filter"
            title={t("label.waiting_list")}
            subtitle={
              filters.waitingList !== undefined ? t("label.waiting_list") : ""
            }
            open={selectedSection === "waitingList"}
          >
            <WaitingListFilter filters={filters} actions={actions} />
          </AccordionItem>
        )}

        {feature_flags?.enable_salesforce_integration && (
          <>
            <AccordionItem
              id="installation-quotation-exists-filter"
              title={t("label.installation_quotation")}
              subtitle={
                filters.installationQuotationExists !== undefined
                  ? translateInstallationQuotationExists(
                      filters.installationQuotationExists
                    )
                  : ""
              }
              open={selectedSection === "installationQuotationExists"}
            >
              <InstallationQuotationExistsFilter
                filters={filters}
                actions={actions}
              />
            </AccordionItem>
            <AccordionItem
              id="installation-quotation-status-filter"
              title={t("label.installation_quotation_status")}
              subtitle={getTranslationSubtitle(
                filters.installationQuotationStatus,
                (item) => translateInstallationQuotationStatus(item) ?? ""
              )}
              open={selectedSection === "installationQuotationStatus"}
            >
              <InstallationQuotationStatusFilter
                filters={filters}
                actions={actions}
              />
            </AccordionItem>
            <AccordionItem
              id="workOrder-status-filter"
              title={t("label.workorder_status")}
              subtitle={getTranslationSubtitle(
                filters.workOrderStatus,
                (item) => item ?? ""
              )}
              open={selectedSection === "workOrderStatus"}
            >
              <WorkOrderStatusFilter filters={filters} actions={actions} />
            </AccordionItem>
            <AccordionItem
              id="workOrder-type-filter"
              title={t("label.workorder_type")}
              subtitle={getTranslationSubtitle(
                filters.workOrderType,
                (item) => item ?? ""
              )}
              open={selectedSection === "workOrderType"}
            >
              <WorkOrderTypeFilter filters={filters} actions={actions} />
            </AccordionItem>
          </>
        )}
        {kic_config?.enabled && (
          <>
            <AccordionItem
              id="installation-type-filter"
              title={t("label.installation_type")}
              subtitle={getTranslationSubtitle(
                filters.installationType,
                (item) => translateInstallationType(item) ?? ""
              )}
              open={selectedSection === "installationType"}
            >
              <InstallationTypeFilter filters={filters} actions={actions} />
            </AccordionItem>

            <DateFilterAccordionItem
              dates={filters.installationDate ?? []}
              actions={actions}
              open={selectedSection === "installationDate"}
              option={DateFilterOption.INSTALLATION_DATE}
              id="installation-date-filter"
              title={t("label.installation_date")}
              allowFutureDates
            />
          </>
        )}
        <DateFilterAccordionItem
          open={selectedSection === "expectedPurchaseDate"}
          dates={filters.expectedPurchaseDateFilter ?? []}
          actions={actions}
          option={DateFilterOption.EXPECTED_PURCHASE_DATE}
          id="expected-purchase-date-filter"
          title={t("label.expected_purchase_date")}
          allowFutureDates
        />
        <AccordionItem
          id="budget-filter"
          title={t("label.budget")}
          subtitle={formatAmountRange(
            filters.budgetFilter,
            getCurrencyCode(general),
            locale,
            Boolean(general?.localization?.disable_decimals)
          )}
          open={selectedSection === "budget"}
        >
          <ValueFilter
            filters={filters}
            actions={actions}
            option={AmountFilterOption.BUDGET}
          />
        </AccordionItem>
      </Accordion>
    </ModalSheet>
  );
};
